import React, { useEffect, useRef, useState } from "react";
import { Box, Text, Pressable, Image, Spinner } from "native-base";
import { StyleSheet, Platform, Alert, TouchableOpacity } from "react-native";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "../../../../../ThemeProvider";
import { corTextoSobre } from "../../../../theme/cores";
import ButtonPersonalizado from "../../../../../components/ButtonPersonalizado";
import Api from "../../../../../Api/Api";
import { useToastMessage } from "../../../../hooks/useToastMessage";
import ModalConfirmacao from "../../../../../components/ModalConfirmacao";
import { CommonActions, useNavigation } from "@react-navigation/native";
import Loader from "../../../../../components/Loader";

export interface PagamentoPixProps {
  valor?: string;
  qrCodeImageUrl?: string;
  getConfigEmpresa?: any;
  onCopiarPix?: (codigo: string) => void;
  nome?: string;
  telefone?: string;
  email?: string;
  cpf?: string;
  dataNascimento?: string;
  hashUrl?: string;
  setAlertMessage: (message: string) => void;
  nomePlano?: string; // só para exibição
}

const CODIGO_PIX_PLACEHOLDER = `00020126360014BRR.GOV.BCB
326871000049356984124023
12381926382937248090211
13920938382932`;

const PagamentoPix: React.FC<PagamentoPixProps> = ({
  valor = "35,00",
  qrCodeImageUrl,
  getConfigEmpresa,
  onCopiarPix,
  nome,
  telefone,
  email,
  cpf,
  dataNascimento,
  hashUrl,
  setAlertMessage,
  nomePlano,
}) => {
  const corBotao = getConfigEmpresa?.coresPWA?.corPrimaria ?? "#0d9488";
  const { primaryColor } = useTheme();
  const corPrincipal: string = primaryColor || "#006400";
  const [data, setData] = useState<any>(null);
  const [tempoRestante, setTempoRestante] = useState<number | null>(null);
  const [tempoEsgotado, setTempoEsgotado] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const [loadingPagamento, setLoadingPagamento] = useState(false);

  const { success, error } = useToastMessage();
  const refModalConfirmacao = useRef<any>(null);
  const navigation = useNavigation();

  // Limpa os intervals ao desmontar o componente
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Formata segundos para MM:SS
  const formatarTempo = (segundos: number) => {
    const min = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${min.toString().padStart(2, "0")}:${seg.toString().padStart(2, "0")}`;
  };

  const getStatusMensagem = () => {
    if (!data) return "Aguarde...";
    if (data?.status === "PAGO") return "Seu plano já está ativo, aproveite!";
    if (data?.status === "FALHA")
      return "Ocorreu um erro ao gerar o PIX, tente novamente.";
    if (tempoEsgotado)
      return "O tempo para realizar o pagamento expirou. Tente novamente.";
    return "Assim que o pagamento for confirmado o atendimento será liberado.";
  };

  const getModalTitulo = () => {
    if (tempoEsgotado) return "Tempo esgotado";
    if (data?.status === "PAGO") return "Tudo certo!";
    return "Ops! Ocorreu um erro";
  };

  const getModalMensagem = () => {
    if (tempoEsgotado)
      return "O tempo para realizar o pagamento expirou. Por favor, tente novamente.";
    if (data?.status === "PAGO")
      return "Pagamento confirmado com sucesso, seu plano já está ativo, aproveite!";
    return "Ocorreu um erro ao gerar o PIX, tente novamente.";
  };

  const handleCopiar = () => {
    if (
      Platform.OS === "web" &&
      typeof navigator !== "undefined" &&
      navigator.clipboard
    ) {
      console.log("codigoPix", data?.qrCode);
      navigator.clipboard.writeText(data?.qrCode).then(
        () =>
          success(
            "Copiado",
            "Código PIX copiado para a área de transferência.",
          ),
        () => error("Erro", "Não foi possível copiar."),
      );
    } else {
      error(
        "Erro",
        "Código PIX: use a função de copiar do seu app quando a integração estiver disponível.",
      );
    }
  };

  useEffect(() => {
    setLoadingPagamento(true);
    if (handleFinishPix) {
      handleFinishPix();
    }
  }, []);

  const handleFinishPix = async () => {
    const data = {
      nome,
      telefone,
      email,
      cpf,
      dataNascimento,
      codTransacao: hashUrl,
      hash: hashUrl,
    };

    const resultado = await Api.gerarPix(data);

    setLoadingPagamento(false);
    if (!resultado.status) {
      setAlertMessage(resultado?.error_details?.message || "Erro ao gerar PIX");
      return;
    }
    setData(resultado.data);
    if (resultado.data?.tempoRestante != null) {
      setTempoRestante(resultado.data.tempoRestante);
    }
    if (resultado.data?.status === "PAGO") {
      refModalConfirmacao.current.abrirModal();
      return;
    } else if (resultado.data?.status === "FALHA") {
      refModalConfirmacao.current.abrirModal();
      return;
    }
    validarPagamento();

    // Inicia polling a cada 1s para consultar status do PIX
  };

  const validarPagamento = async () => {
    const codTransacao = hashUrl;
    // const codTransacao = hashUrl || resultado.data?.id || resultado.data?.codTransacao;
    if (!codTransacao) return;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      const res = await Api.consultarPix(codTransacao);

      if (res.status && res.data?.tempoRestante != null) {
        setTempoRestante(res.data.tempoRestante);
      }
      if (res.status && res.data?.status === "PAGO") {
        setData(res.data);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        refModalConfirmacao.current.abrirModal();
      }
    }, 3000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  };

  // Contador regressivo do tempo restante
  useEffect(() => {
    if (
      tempoRestante == null ||
      tempoRestante <= 0 ||
      data?.status === "PAGO" ||
      data?.status === "FALHA" ||
      tempoEsgotado
    ) {
      return;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setTempoRestante((prev) => {
        if (prev == null || prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setTempoEsgotado(true);
          refModalConfirmacao.current?.abrirModal();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [tempoRestante, data?.status, tempoEsgotado]);

  // ---------------- Somente apresentação (layout novo) ----------------
  // Separa o valor em reais e centavos só para exibir (ex.: "R$ 250,00").
  const partesValor = (() => {
    if (data?.valor == null || data?.valor === "") return null;
    const texto =
      typeof data.valor === "number"
        ? data.valor.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : String(data.valor).replace("R$", "").trim();
    const [reais, centavos] = texto.split(",");
    return { reais, centavos: centavos ?? "00" };
  })();

  const textoStatus = !data
    ? "Gerando pagamento"
    : data?.status === "PAGO"
      ? "Pagamento confirmado"
      : data?.status === "FALHA"
        ? "Erro ao gerar PIX"
        : "Aguardando pagamento";

  const exibeTempo =
    tempoRestante != null &&
    tempoRestante > 0 &&
    data?.status !== "PAGO" &&
    data?.status !== "FALHA" &&
    !tempoEsgotado;

  return (
    <Box style={styles.container}>
      {loadingPagamento && <Loader />}

      {/* Cartão principal: valor, QR Code e código copia e cola */}
      <Box style={styles.cardInterno}>
        {partesValor && (
          <Box style={styles.valorRow}>
            <Text style={styles.moeda}>R$</Text>
            <Text style={styles.valorReais}>{partesValor.reais}</Text>
            <Text style={styles.valorCentavos}>,{partesValor.centavos}</Text>
          </Box>
        )}
        {!!nomePlano && (
          <Text style={styles.descricaoValor}>Plano {nomePlano}</Text>
        )}

        {/* QR Code */}
        <Box style={styles.qrContainer}>
          {data?.qrCodeUrl ? (
            <Image
              source={{ uri: data?.qrCodeUrl }}
              alt="QR Code PIX"
              style={styles.qrImage}
              resizeMode="contain"
            />
          ) : (
            <Box style={styles.qrPlaceholder}>
              <MaterialCommunityIcons
                name="qrcode-scan"
                size={80}
                color="#d1d5db"
              />
            </Box>
          )}
        </Box>

        {/* Código PIX para copiar */}
        <Box style={styles.codigoBox}>
          <Text
            style={styles.codigoTexto}
            numberOfLines={2}
            selectable
          >
            {data?.qrCode}
          </Text>
        </Box>

        <TouchableOpacity
          activeOpacity={0.85}
          onPress={handleCopiar}
          style={[styles.botaoCopiar, { backgroundColor: corPrincipal }]}
        >
          <Feather
            name="copy"
            size={17}
            color={corTextoSobre(corPrincipal)}
          />
          <Text
            style={[
              styles.textoBotaoCopiar,
              { color: corTextoSobre(corPrincipal) },
            ]}
          >
            Copiar código Pix
          </Text>
        </TouchableOpacity>
      </Box>

      {/* Status do pagamento */}
      <Box style={styles.statusBox}>
        <Spinner size="sm" color={corPrincipal} />
        <Box style={{ flex: 1 }}>
          <Text style={styles.textoStatus}>{textoStatus}</Text>
          <Text style={styles.statusMensagem}>{getStatusMensagem()}</Text>
        </Box>
        {exibeTempo && (
          <Box style={styles.tempoBox}>
            <Text style={styles.tempo}>{formatarTempo(tempoRestante)}</Text>
            <Text style={styles.rotuloTempo}>para pagar</Text>
          </Box>
        )}
      </Box>

      <ModalConfirmacao
        titulo={getModalTitulo()}
        ref={refModalConfirmacao}
        semBotaoFechar={true}
        mensagem={getModalMensagem()}
        textoBotaoPositivo={"OK"}
        onConfirmPositivo={() => {
          if (refModalConfirmacao.current) {
            refModalConfirmacao.current.fecharModal();
          }
          if (tempoEsgotado || data?.status === "FALHA") {
            window.history.pushState(
              {},
              "",
              "/preencher-link-pagamento?hash=" + hashUrl,
            );
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  { name: "TabNavigator", params: { screen: "Inicio" } },
                ],
              }),
            );
            return;
          }
          if (typeof window !== "undefined") {
            window.history.pushState({}, "", "/");
          }
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: "TabNavigator", params: { screen: "Inicio" } }],
            }),
          );
        }}
      />
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    alignSelf: "center",
  },
  cardInterno: {
    backgroundColor: "#fff",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    padding: 18,
    marginBottom: 14,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  valorRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  moeda: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1d1d1d",
    marginTop: 10,
    marginRight: 3,
  },
  valorReais: {
    fontSize: 36,
    lineHeight: 42,
    fontWeight: "800",
    color: "#1d1d1d",
  },
  valorCentavos: {
    fontSize: 16,
    fontWeight: "800",
    color: "#1d1d1d",
    marginTop: 6,
  },
  descricaoValor: {
    fontSize: 12,
    color: "#7c7c7c",
    marginTop: 2,
  },
  qrContainer: {
    alignItems: "center",
    marginTop: 16,
    marginBottom: 16,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
  },
  qrImage: {
    width: 180,
    height: 180,
  },
  qrPlaceholder: {
    width: 180,
    height: 180,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  codigoBox: {
    width: "100%",
    backgroundColor: "#F7F4EE",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  codigoTexto: {
    fontSize: 12,
    color: "#4b5563",
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
  },
  botaoCopiar: {
    width: "100%",
    height: 50,
    borderRadius: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  textoBotaoCopiar: {
    fontSize: 15,
    fontWeight: "700",
  },
  statusBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 14,
    borderRadius: 18,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  textoStatus: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1d1d1d",
  },
  statusMensagem: {
    fontSize: 12,
    lineHeight: 16,
    color: "#7c7c7c",
    marginTop: 2,
  },
  tempoBox: {
    alignItems: "flex-end",
  },
  tempo: {
    fontSize: 20,
    fontWeight: "800",
    color: "#1d1d1d",
  },
  rotuloTempo: {
    fontSize: 11,
    color: "#7c7c7c",
  },
});

export default PagamentoPix;
